import { isBuildImage } from '../../core/imageBuild'
import { useId, type MouseEvent, type ReactNode } from 'react'
import { CircleCheck, CircleX, TriangleAlert } from 'lucide-react'

import { Button } from '../../ui/Button'
import { Card } from '../../ui/Card'
import { Rate } from '../../ui/Rate'
import { priceCapSummary, type GuidedDraft } from '../../core/guided'
import { hostDisplayName } from '../../core/hostName'
import { parseImageRef } from '../../core/imageRef'
import { BLOCK_SECONDS, formatUsdMicros, formatUsdMicrosPerHour, formatUsdPerHour, parseChainAmount, usdMicrosPerHour } from '../../core/pricing'
import { hostProductBreakdown, priceForDraft, type CapacityPrice, type OfferCapacityLike, type PriceLine, type UnitRates } from '../../core/rateCard'
import { capReadiness, type ReadinessItem } from '../../core/readiness'
import { isExclusive, isHostProduct, type RentalMode } from '../../core/rental'
import { normalizedReplicaCount } from '../../core/capacityEnvelope'
import { coresNumber, formatCores } from '../../core/sizing'
import type { CatalogHostDto } from '../../data/types'

export const OPEN_REQUEST_NOTE = 'You set no maximum. Offers can arrive at any price, and you choose before accepting one.'

/**
 * What the price figure in the summary is, by mode. The label always says
 * which kind of number it is (`core/rental` PricingSource): a host's posted
 * rate is what the host's quote will be based on, a tenant maximum is a
 * ceiling, and a custom-capacity request has no number until the host quotes.
 * A cap is never called an estimate or a quote.
 */
export type SummaryPrice = {
  label: string
  /** The full figure for the card, or undefined when there is none to show. */
  figure?: ReactNode
  /** The figure in one line for the mobile bar; falls back to the label. */
  compact: string
  note: string
  caution?: boolean
  /** The working behind the figure, line by line, in dollars per hour. */
  breakdown?: { heading: string; lines: { id: string; label: string; amount: string }[] }
}

function quantityText(line: PriceLine): string {
  if (line.axis === 'cpu') {
    const milli = Number(line.quantityMilli ?? line.quantity * 1000n)
    return milli === 1000 ? '1 core' : `${coresNumber(milli)} cores`
  }
  if (line.axis === 'gpu') return line.quantity === 1n ? '1 GPU' : `${line.quantity} GPUs`
  return `${line.quantity} GB ${line.axis === 'memory' ? 'memory' : 'storage'}`
}

/**
 * The working, line by line, in the host's own dollars per hour — and a last
 * line for whatever the charged rate adds on top, so the lines always add up to
 * the figure above them.
 *
 * The host prices per hour and the chain charges whole uddt per block, so the
 * charged rate is the exact sum rounded up to the next block unit: at most
 * $0.0006/hour. For an exclusive host the remainder is mostly the premium,
 * and is named that.
 */
function breakdownLines(price: CapacityPrice, chargedPerBlock: bigint, exclusive = false): { id: string; label: string; amount: string }[] {
  const lines: { id: string; label: string; amount: string }[] = price.lines.map((line) => ({ id: line.axis, label: `${quantityText(line)} × ${formatUsdMicros(line.unitPerHour)}`, amount: formatUsdMicros(line.totalPerHour) }))
  const charged = usdMicrosPerHour({ denom: 'uddt', amount: chargedPerBlock.toString() }, BLOCK_SECONDS)
  const listed = price.lines.reduce((sum, line) => sum + line.totalPerHour, 0n)
  if (charged !== null && charged > listed) {
    lines.push(exclusive
      ? { id: 'premium', label: 'Exclusive premium', amount: formatUsdMicros(charged - listed) }
      : { id: 'rounding', label: 'Rounded up to the chain\'s per-block rate', amount: formatUsdMicros(charged - listed) })
  }
  return lines
}

function hourlyCapText(priceCap: string): string {
  try {
    return formatUsdMicrosPerHour(BigInt(priceCapSummary(priceCap.trim()).hourly.amount))
  } catch {
    return 'Unavailable'
  }
}

export function summaryPrice({ draft, rentalMode, openRequest, postedRate, rates = null, offer = null, unsized = false }: {
  draft: GuidedDraft
  rentalMode: RentalMode | undefined
  openRequest: boolean
  postedRate: string | null | undefined
  /** The bound host's published rate card, when the catalog carries one. */
  rates?: UnitRates | null | undefined
  /** The host offer a host product is priced on. */
  offer?: { allocatable?: OfferCapacityLike | null; available?: OfferCapacityLike | null } | null | undefined
  /** Custom capacity with a resource not chosen yet: there is no size to price. */
  unsized?: boolean
}): SummaryPrice {
  if (openRequest) return { label: 'No maximum', compact: 'No maximum', note: OPEN_REQUEST_NOTE }
  if (rentalMode === undefined) {
    return { label: 'Maximum from your link', compact: 'Maximum from your link', note: 'Hosts can offer at or below the maximum carried by this link; you choose an offer.' }
  }
  if (rentalMode === 'RENTAL_MODE_COMPETITIVE') {
    const text = hourlyCapText(draft.priceCap)
    return { label: 'Your maximum', figure: <span className="money">{text}</span>, compact: `Your maximum ${text}`, note: 'Offers arrive at or below this. It is a ceiling, not the price.' }
  }
  if (rentalMode === 'RENTAL_MODE_DIRECT_CUSTOM' && unsized) {
    return { label: 'Priced by the host', compact: 'Choose a size', note: 'Choose CPU, memory and storage to see the price from this host\'s rates.' }
  }
  if (rentalMode === 'RENTAL_MODE_DIRECT_CUSTOM') {
    // Custom capacity is priced on exactly what is asked for, so the host's
    // published rates give the price the quote will carry, live with the sliders.
    const count = normalizedReplicaCount(draft)
    const price = rates ? priceForDraft(rates, draft, count) : null
    if (!price || price.totalPerBlock <= 0n) return {
      label: 'Priced by the host',
      compact: 'Price unavailable',
      caution: true,
      note: 'This provider has no usable current rate card for this custom rental. Choose another host or try again when its rates are available.',
    }
    const coin = { denom: 'uddt', amount: price.totalPerBlock.toString() }
    return {
      label: 'Price for this size',
      figure: <>{<Rate coin={coin} />}{count > 1 && <span className="summary-price-count"> for {count} replicas</span>}</>,
      compact: formatUsdPerHour(coin, BLOCK_SECONDS) ?? 'Unavailable',
      breakdown: { heading: count > 1 ? `From the host's rates, for ${count} replicas` : 'From the host\'s rates', lines: breakdownLines(price, price.totalPerBlock) },
      note: 'Worked out from the rates this host publishes. Its quote on the next screen confirms it before you pay.',
    }
  }
  // A host product. Undefined means the host's offer has not been read yet,
  // which is not the same fact as "this host publishes no price".
  if (postedRate === undefined) {
    return { label: 'Priced by the host', compact: 'Priced by the host', note: 'The provider signs a quote after configuration; review it before signing.' }
  }
  if (postedRate === null) {
    return isExclusive(rentalMode)
      ? { label: 'No exclusive price', compact: 'No exclusive price', caution: true, note: 'This host does not publish an exclusive price, so it cannot be rented whole. Choose its free capacity or another host.' }
      : { label: 'No posted price', compact: 'No posted price', caution: true, note: 'This host does not publish a price for this product. Choose another host.' }
  }
  // The provider charges a host product's posted rate once
  // (`RateCard.PriceForProduct` has no count), and the form offers no replicas
  // on a host product, so the figure is the posted rate itself.
  const perBlock = parseChainAmount(postedRate)
  const coin = perBlock === null ? null : { denom: 'uddt', amount: perBlock.toString() }
  const rate = <Rate coin={coin} />
  const compactRate = formatUsdPerHour(coin, BLOCK_SECONDS) ?? 'Unavailable'
  // The working behind the posted rate: this host's rates applied to the
  // capacity the product is priced on, which is the host's, not the sliders'.
  const breakdown = rates && offer ? hostProductBreakdown(rates, isExclusive(rentalMode), offer, postedRate) : null
  return {
    label: 'Host\'s posted rate',
    figure: rate,
    compact: compactRate,
    ...(breakdown ? {
      breakdown: {
        heading: isExclusive(rentalMode) ? 'The whole machine' : 'Everything free on this host',
        lines: breakdownLines(breakdown, breakdown.totalPerBlock + (breakdown.premiumPerBlock ?? 0n), isExclusive(rentalMode)),
      },
    } : {}),
    note: 'The host signs an exact quote next. You review it before paying.',
  }
}

/**
 * Move to a form card from a summary link: scroll it into view, then put focus
 * on its heading so keyboard and screen-reader users land where they looked.
 * Without script the plain `#card-…` fragment still works.
 */
export function jumpToCard(event: MouseEvent<HTMLAnchorElement>, cardId: string) {
  const card = document.getElementById(cardId)
  if (!card) return
  event.preventDefault()
  const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  card.scrollIntoView?.({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
  const heading = card.querySelector('h2')
  if (heading) {
    heading.setAttribute('tabindex', '-1')
    heading.focus({ preventScroll: true })
  }
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return <div><dt>{label}</dt><dd>{children}</dd></div>
}

function Group({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  const id = useId()
  return <div className="summary-group" role="group" aria-labelledby={id}>
    <p className="summary-group-title"><span id={id}>{title}</span>{action}</p>
    <dl>{children}</dl>
  </div>
}

function ImageText({ image }: { image: string }) {
  if (isBuildImage(image)) return <span>Build from Dockerfile</span>
  const ref = parseImageRef(image)
  if (!ref) return <code className="summary-mono">{image || 'Not set'}</code>
  // An unpinned reference shows the tag it will run, in the caution tone. The
  // word "latest" carries the meaning; the colour only reinforces it.
  const base = ref.registry === 'docker.io' ? ref.repository : `${ref.registry}/${ref.repository}`
  if (ref.pinned) return <code className="summary-mono">{image.trim()}</code>
  return <code className="summary-mono">{base}<span className="summary-unpinned">:{ref.tag ?? 'latest'}</span></code>
}

function portsText(ports: GuidedDraft['ports']): string {
  const label = (port: GuidedDraft['ports'][number]) => `${port.container}${port.protocol === 'udp' ? '/udp' : ''}`
  if (ports.length === 0) return 'None'
  if (ports.length > 4) return `${ports.slice(0, 3).map(label).join(' · ')} +${ports.length - 3} more`
  return ports.map(label).join(' · ')
}

/** The replica count is normalised the same way the SDL and the price multiplier read it. */
export function recoveryText(draft: Pick<GuidedDraft, 'recoveryMode' | 'replicaCount'>): string {
  return draft.recoveryMode === 'replicated' ? `${normalizedReplicaCount(draft)} replicas` : draft.recoveryMode === 'standby' ? 'Standby' : 'Single host'
}

const READINESS_ICON = { ok: CircleCheck, warn: TriangleAlert, block: CircleX } as const

export function ReadinessList({ items, submitted }: { items: readonly ReadinessItem[]; submitted: boolean }) {
  const { shown, more } = capReadiness(items)
  return <ul className={`readiness${submitted ? ' readiness--submitted' : ''}`} aria-live="polite" aria-label="Readiness">
    {shown.map((item) => {
      const Icon = READINESS_ICON[item.level]
      const body = <><Icon aria-hidden="true" size={16} /><span>{item.text}</span></>
      return <li key={item.id} className={`readiness-item readiness-${item.level}`}>
        {item.targetId ? <a href={`#${item.targetId}`}>{body}</a> : body}
      </li>
    })}
    {more > 0 && <li className="readiness-item readiness-more">and {more} more</li>}
  </ul>
}

export function ConfigSummary({ draft, rentalMode, sizingHost, price, readiness, submitted, continueLabel, sizeLocked = false }: {
  draft: GuidedDraft
  rentalMode: RentalMode | undefined
  /** Only the host a direct request is bound to. Never the `?host=` a competitive request arrived with. */
  sizingHost: CatalogHostDto | null
  /** From `summaryPrice`, computed once by the page and shared with the mobile bar. */
  price: SummaryPrice
  readiness: readonly ReadinessItem[]
  submitted: boolean
  continueLabel: string
  /** A host product's size is the host's; the link then explains rather than offers an edit. */
  sizeLocked?: boolean
}) {
  const competitive = rentalMode === 'RENTAL_MODE_COMPETITIVE'
  const hostProduct = rentalMode !== undefined && isHostProduct(rentalMode)
  const variables = draft.env.filter((row) => row.key.trim() !== '').length

  return <aside className="config-summary">
    <Card title="Summary">
      <Group title="Workload">
        <Row label="Name">{draft.name || 'Unnamed machine'}</Row>
        <Row label="Image"><ImageText image={draft.image} /></Row>
      </Group>
      <Group title="Where">
        {competitive
          ? <Row label="Host"><span className="muted">Chosen from offers</span></Row>
          : <Row label="Host">{sizingHost ? hostDisplayName(sizingHost) : <span className="muted">Assigned when an offer is accepted</span>}</Row>}
        {hostProduct && rentalMode && <Row label="Machine">{isExclusive(rentalMode) ? 'Exclusive' : 'Shared'}{sizingHost?.region ? ` · ${sizingHost.region}` : ''}</Row>}
      </Group>
      <Group title="Resources" action={sizeLocked
        ? <a className="summary-edit" href="#card-size" onClick={(event) => jumpToCard(event, 'card-size')}>Why fixed?</a>
        : <a className="summary-edit" href="#card-size" aria-label="Edit resources" onClick={(event) => jumpToCard(event, 'card-size')}>Edit</a>}>
        <Row label="CPU">{formatCores(draft.cpu)}</Row>
        <Row label="Memory">{draft.ram} GB</Row>
        <Row label="Storage">{draft.storage} GB</Row>
        {draft.volume && <Row label="Volume">{draft.volume.sizeGi} GB at <code className="summary-mono">{draft.volume.mount}</code></Row>}
        <Row label="Ports"><span className="summary-ports"><code className="summary-mono">{portsText(draft.ports)}</code> <a className="summary-edit" href="#card-networking" aria-label="Edit ports" onClick={(event) => jumpToCard(event, 'card-networking')}>Edit</a></span></Row>
        <Row label="Recovery">{recoveryText(draft)}</Row>
        {variables > 0 && <Row label="Variables">{variables} set</Row>}
      </Group>
      <div id="config-provider-price" className={`summary-price${price.caution ? ' summary-price--caution' : ''}`}>
        <p className="summary-price-label">{price.label}</p>
        {price.figure && <p className="summary-price-figure">{price.figure}</p>}
        {price.breakdown && <div className="summary-price-breakdown">
          <p className="summary-price-breakdown-title">{price.breakdown.heading}</p>
          <dl>{price.breakdown.lines.map((line) => <div key={line.id}><dt>{line.label}</dt><dd>{line.amount}</dd></div>)}</dl>
        </div>}
        <p className="summary-price-note">{price.note}</p>
      </div>
      {readiness.length > 0 && <ReadinessList items={readiness} submitted={submitted} />}
      <Button type="submit" form="configure-form" variant="primary" block className="summary-continue">{continueLabel}</Button>
    </Card>
  </aside>
}
